import { useCallback, useEffect } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { GroupedSummaryDto } from './types';
import { useProductivitySummaryQuery, useSummaryApisEnabled, useSummaryApisSelectionResolved } from './index';
import { useSummaryNoDataDiagnostics } from './useSummaryNoDataDiagnostics';

type LegacyQueryHook<TData, TParams extends object> = (
  params: TParams & { enabled?: boolean }
) => UseQueryResult<TData>;

type UseDualProductivityDataOptions<TData, TParams extends object> = {
  legacyParams: TParams;
  useLegacyQuery: LegacyQueryHook<TData, TParams>;
  mapSummary: (grouped: GroupedSummaryDto | undefined) => TData | undefined;
  diagnosticTag?: string;
};

type UseDualProductivityDataResult<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  summaryEnabled: boolean;
  refetch: UseQueryResult<TData>['refetch'];
};

/**
 * Provides a unified data source for productivity tiles by switching between
 * legacy APIs and summary APIs behind the summary feature flag.
 *
 * CRITICAL: In summary mode, the legacy query MUST be completely disabled.
 * When summaryEnabled is true, refetch must ONLY call refetchSummary.
 */
export function useDualProductivityData<TData, TParams extends object>(
  options: UseDualProductivityDataOptions<TData, TParams>
): UseDualProductivityDataResult<TData> {
  const summaryEnabled = useSummaryApisEnabled();
  const selectionResolved = useSummaryApisSelectionResolved();
  const {
    data: groupedSummary,
    isLoading: isSummaryLoading,
    refetch: refetchSummary,
  } =
    useProductivitySummaryQuery();

  const {
    data: legacyData,
    isLoading: isLegacyLoading,
    refetch: refetchLegacy,
  } = options.useLegacyQuery({
    ...options.legacyParams,
    enabled: selectionResolved && !summaryEnabled,
  });

  // Diagnostic: warn if legacy query is active in summary mode
  useEffect(() => {
    if (summaryEnabled && isLegacyLoading) {
      console.warn(
        '[Summary API Guard] Legacy query is loading in summary mode. This should not happen.',
        {
          domain: 'productivity',
          summaryEnabled,
          diagnosticTag: options.diagnosticTag,
          legacyParams: options.legacyParams,
        }
      );
    }
  }, [summaryEnabled, isLegacyLoading, options.diagnosticTag, options.legacyParams]);

  const data = summaryEnabled
    ? options.mapSummary(groupedSummary)
    : legacyData;

  useSummaryNoDataDiagnostics({
    summaryEnabled,
    isSummaryLoading,
    groupedSummary,
    mappedData: data,
    legacyParams: options.legacyParams,
    domain: 'productivity',
    diagnosticTag: options.diagnosticTag,
  });

  const refetch = useCallback<UseQueryResult<TData>['refetch']>((options) => {
    const selectedRefetch = summaryEnabled ? refetchSummary : refetchLegacy;
    return selectedRefetch(options) as ReturnType<UseQueryResult<TData>['refetch']>;
  }, [summaryEnabled, refetchSummary, refetchLegacy]);

  return {
    data,
    isLoading: !selectionResolved || (summaryEnabled ? isSummaryLoading : isLegacyLoading),
    summaryEnabled,
    refetch,
  };
}
